import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip
} from '@mui/material';
import PortalSnackbar from '../../Common/PortalSnackbar';
import { ArrowLeft, Eye } from '@phosphor-icons/react';
import api from '../../../utils/api';
import LeadCoupleAutocomplete from '../../Common/LeadCoupleAutocomplete';
import ProposalCraftEditor from './ProposalCraftEditor';
import {
  ProposalLineItemModal,
  ProposalBillingScheduleModal,
  ProposalTermsModal
} from './ProposalPricingDialogs';
import { craftJsonToBlocks, filterLayoutBlocks, normaliseLayoutBlocks } from '../../../utils/proposalCraftUtils';
import { buildProposalPreviewPayload, openProposalSupplierPreview } from '../../../utils/openProposalPreview';
import { getLineItemTotal, getUnitPriceWithTax } from '../../../utils/taxCalculations';
import { useBusiness } from '../../../context/BusinessContext';
import { usePlatformAlerts } from '../../../context/PlatformAlertsContext';

const getLegacyTemplateBlocks = (template) => {
  const rawContent = template?.content;

  if (Array.isArray(rawContent?.blocks) && rawContent.blocks.length > 0) {
    return normaliseLayoutBlocks(rawContent.blocks);
  }

  if (rawContent) {
    const parsedFromCraft = normaliseLayoutBlocks(craftJsonToBlocks(rawContent));
    if (parsedFromCraft.length > 0) return parsedFromCraft;
  }

  const messageText = (template?.message || '').trim();
  if (messageText) {
    return [
      {
        type: 'paragraph',
        props: { text: messageText }
      }
    ];
  }

  return [];
};

const getLegacyTemplateTerms = (template) => {
  const explicitTerms = (template?.termsText || '').trim();
  if (explicitTerms) return explicitTerms;

  const extractTermsFromCraftPayload = (rawContent) => {
    if (!rawContent) return '';
    try {
      const parsed = typeof rawContent === 'string' ? JSON.parse(rawContent) : rawContent;
      if (!parsed || typeof parsed !== 'object') return '';
      const nodes = Object.values(parsed);
      for (const node of nodes) {
        const resolvedName = node?.type?.resolvedName;
        if (resolvedName === 'TermsBlock') {
          const text = node?.props?.text || node?.props?.termsText || '';
          const trimmed = String(text).trim();
          if (trimmed) return trimmed;
        }
      }
      return '';
    } catch {
      return '';
    }
  };

  const termsFromBlocks = Array.isArray(template?.content?.blocks)
    ? template.content.blocks.find((b) => b?.type === 'terms')?.props?.text
    : '';
  const fallbackTerms = (termsFromBlocks || '').trim();
  if (fallbackTerms) return fallbackTerms;

  return extractTermsFromCraftPayload(template?.content);
};

const formatMoney = (amount, currency = 'GBP') => {
  const n = typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0;
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(n);
  } catch {
    return `£${n.toFixed(2)}`;
  }
};

function ProposalForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const templateIdFromUrl = searchParams.get('templateId');
  const isEdit = Boolean(id);
  const { selectedBusiness } = useBusiness();
  const { showError } = usePlatformAlerts();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    leadId: '',
    message: '',
    termsText: '',
    currency: 'GBP',
    lineItems: [],
    billingSchedule: [{ label: 'Deposit', amountType: 'percentage', value: 30, dueType: 'immediate', dueValue: 0 }],
    content: { blocks: [] },
    additionalInformationFields: []
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState(templateIdFromUrl || '');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const craftEditorRef = useRef(null);
  const [lineItemModal, setLineItemModal] = useState({ open: false, index: null });
  const [scheduleModal, setScheduleModal] = useState({ open: false, index: null });
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [agreementMeta, setAgreementMeta] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendingAgreement, setSendingAgreement] = useState(false);

  const businessId = selectedBusiness?.business?._id;

  const fetchProducts = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await api.get(`/api/products/business/${businessId}`);
      setProducts(res.data || []);
    } catch {
      setProducts([]);
    }
  }, [businessId]);

  const fetchProposal = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/agreements/${id}`);
      const p = res.data;
      setAgreementMeta(p);
      const loadedBlocks = p.content?.blocks || [];
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('[ProposalForm] Loaded content.blocks from API:', loadedBlocks.length, loadedBlocks);
      }
      setFormData({
        leadId: p.lead?._id || p.lead,
        message: p.message || '',
        termsText: p.termsText || '',
        currency: p.currency || 'GBP',
        lineItems: (p.lineItems || []).length > 0
          ? p.lineItems.map((i) => ({
              ...(i._id ? { _id: i._id } : {}),
              productId: i.productId || '',
              description: i.description || '',
              quantity: i.quantity || 1,
              unitPrice: i.unitPrice ?? '',
              taxPercentage: i.taxPercentage ?? 0,
              priceIncludesTax: Boolean(i.priceIncludesTax),
              isOptional: !!i.isOptional
            }))
          : [],
        billingSchedule: (p.billingSchedule || []).length > 0
          ? p.billingSchedule.map((s) => ({
              label: s.label || '',
              amountType: s.amountType || 'percentage',
              value: s.value ?? 30,
              dueType: s.dueType || 'immediate',
              dueValue: s.dueValue ?? 0
            }))
          : [{ label: 'Deposit', amountType: 'percentage', value: 30, dueType: 'immediate', dueValue: 0 }],
        content: loadedBlocks.length
          ? {
              blocks: normaliseLayoutBlocks(loadedBlocks)
            }
          : { blocks: [] },
        additionalInformationFields: p.additionalInformationFields || []
      });
    } catch {
      setError('Failed to load agreement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (templateIdFromUrl) setSelectedTemplateId(templateIdFromUrl);
  }, [templateIdFromUrl]);

  useEffect(() => {
    if (businessId) {
      fetchProducts();
      if (isEdit) fetchProposal();
    }
  }, [businessId, isEdit, fetchProducts, fetchProposal]);

  useEffect(() => {
    const message = location.state?.savedMessage;
    if (!message) return;
    setSuccess(message);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!selectedTemplateId || isEdit) return;
    const loadTemplate = async () => {
      setLoadingTemplate(true);
      try {
        const res = await api.get(`/api/agreements/${selectedTemplateId}`);
        const t = res.data;
        const hydratedBlocks = getLegacyTemplateBlocks(t);
        const hydratedTerms = getLegacyTemplateTerms(t);
        setFormData((prev) => ({
          ...prev,
          message: t.message || '',
          termsText: hydratedTerms,
          currency: t.currency || 'GBP',
          lineItems: (t.lineItems || []).length > 0
            ? t.lineItems.map((i) => ({
                ...(i._id ? { _id: i._id } : {}),
                productId: i.productId || '',
                description: i.description || '',
                quantity: i.quantity || 1,
                unitPrice: i.unitPrice ?? '',
                taxPercentage: i.taxPercentage ?? 0,
                isOptional: !!i.isOptional
              }))
            : [],
          billingSchedule: (t.billingSchedule || []).length > 0
            ? t.billingSchedule.map((s) => ({
                label: s.label || '',
                amountType: s.amountType || 'percentage',
                value: s.value ?? 30,
                dueType: s.dueType || 'immediate',
                dueValue: s.dueValue ?? 0
              }))
            : [{ label: 'Deposit', amountType: 'percentage', value: 30, dueType: 'immediate', dueValue: 0 }],
          content: hydratedBlocks.length > 0 ? { blocks: hydratedBlocks } : prev.content,
          additionalInformationFields: t.additionalInformationFields || []
        }));
      } catch {
        setError('Failed to load template');
      } finally {
        setLoadingTemplate(false);
      }
    };
    loadTemplate();
  }, [selectedTemplateId, isEdit]);

  const handleSaveLineItem = (record) => {
    setFormData((prev) => {
      const next = [...prev.lineItems];
      const row = {
        ...record,
        unitPrice: record.unitPrice === '' || record.unitPrice === null ? '' : String(record.unitPrice)
      };
      const idx = lineItemModal.index;
      if (idx === null) {
        next.push(row);
      } else {
        next[idx] = row;
      }
      return { ...prev, lineItems: next };
    });
  };

  const handleSaveSchedule = (record) => {
    setFormData((prev) => {
      const next = [...prev.billingSchedule];
      const idx = scheduleModal.index;
      if (idx === null) {
        next.push(record);
      } else {
        next[idx] = record;
      }
      return { ...prev, billingSchedule: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.leadId) {
      const message = 'Please add a lead to this agreement before saving.';
      setError(message);
      showError(message);
      return;
    }
    const validItems = formData.lineItems.filter((i) => (i.description || '').trim() && (i.unitPrice !== '' && i.unitPrice !== null && !isNaN(parseFloat(i.unitPrice))));
    if (validItems.length === 0) {
      const message = 'Add at least one line item with description and price';
      setError(message);
      showError(message);
      return;
    }

    const blocksFromEditor = craftEditorRef.current?.getBlocks?.();
    const contentToSave = formData.content || { blocks: [] };
    const raw = Array.isArray(blocksFromEditor) ? blocksFromEditor : (contentToSave.blocks || []);
    const blocks = filterLayoutBlocks(raw);
    const blockCount = blocks.length;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[ProposalForm] Save blocks count:', blockCount, 'fromEditor:', !!blocksFromEditor);
    }
    const payload = {
      businessId,
      leadId: formData.leadId,
      templateId: selectedTemplateId || undefined,
      message: formData.message.trim(),
      termsText: formData.termsText.trim(),
      currency: formData.currency,
      lineItems: validItems.map((i) => ({
        ...(i._id ? { _id: i._id } : {}),
        productId: i.productId || undefined,
        description: i.description.trim(),
        quantity: Number(i.quantity) || 1,
        unitPrice: parseFloat(i.unitPrice) || 0,
        taxPercentage: parseFloat(i.taxPercentage) || 0,
        priceIncludesTax: Boolean(i.priceIncludesTax),
        isOptional: !!i.isOptional
      })),
      billingSchedule: formData.billingSchedule
        .filter((s) => (s.label || '').trim())
        .map((s) => ({
          label: s.label.trim(),
          amountType: s.amountType,
          value: parseFloat(s.value) || 0,
          dueType: s.dueType,
          dueValue: parseInt(s.dueValue, 10) || 0
        })),
      content: { blocks },
      additionalInformationFields: (formData.additionalInformationFields || []).map((f, index) => ({
        type: f.type === 'textarea' ? 'textarea' : 'text',
        label: (f.label || '').trim(),
        placeholder: (f.placeholder || '').trim() || undefined,
        order: typeof f.order === 'number' ? f.order : index
      }))
    };

    setSaving(true);
    try {
      if (isEdit) {
        const { data: updated } = await api.put(`/api/agreements/${id}`, payload);
        setAgreementMeta(updated);
        setSuccess('Agreement saved successfully.');
      } else {
        const { data: created } = await api.post('/api/agreements', payload);
        setAgreementMeta(created);
        if (created?._id) {
          navigate(`/supplier/agreements/${created._id}/edit`, {
            state: { savedMessage: 'Agreement saved successfully.' }
          });
          return;
        }
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save agreement';
      setError(message);
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const currentAgreementId = id || agreementMeta?._id;
  const canSendAgreement = Boolean(currentAgreementId) && !['accepted', 'declined', 'archived'].includes(agreementMeta?.status);
  const sendNowLabel = agreementMeta?.status === 'sent' ? 'Send again now' : 'Send now';
  const sendRecipient = agreementMeta?.lead?.name || agreementMeta?.lead?.email || 'Selected couple';
  const sendRecipientEmail = agreementMeta?.lead?.email || '';
  const sendLineItems = formData.lineItems.filter((item) => (item.description || '').trim());
  const sendTotal = sendLineItems.reduce((sum, item) => sum + getLineItemTotal(item), 0);

  const handleSendNow = async () => {
    if (!currentAgreementId) return;
    setSendingAgreement(true);
    setError('');
    try {
      await api.post(`/api/agreements/${currentAgreementId}/send`);
      setSuccess('Agreement sent successfully.');
      await fetchProposal();
      setSendDialogOpen(false);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to send agreement';
      setError(message);
      showError(message);
    } finally {
      setSendingAgreement(false);
    }
  };

  const handleOpenPreview = () => {
    setError('');
    try {
      const payload = buildProposalPreviewPayload({
        kind: 'agreement',
        formData,
        craftEditorRef,
        businessName: selectedBusiness?.business?.name
      });
      openProposalSupplierPreview(payload);
    } catch (err) {
      const message = err?.message || 'Could not open preview.';
      setError(message);
      showError(message);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form noValidate onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button type="button" startIcon={<ArrowLeft size={18} />} onClick={() => navigate('/supplier/agreements')}>
            Back
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {isEdit ? 'Edit agreement' : 'Create agreement'}
          </Typography>
          {isEdit && (
            <Chip
              size="small"
              label={`Status: ${agreementMeta?.status || 'draft'}`}
              color={agreementMeta?.status === 'scheduled' ? 'warning' : agreementMeta?.status === 'sent' ? 'info' : 'default'}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            type="button"
            variant="outlined"
            startIcon={<Eye size={18} />}
            onClick={handleOpenPreview}
            disabled={loadingTemplate}
          >
            Preview
          </Button>
          <Button variant="contained" type="submit" disabled={saving || loadingTemplate}>
            {saving || loadingTemplate ? <CircularProgress size={24} /> : 'Save Agreement'}
          </Button>
          {canSendAgreement && (
            <Button
              type="button"
              variant="contained"
              onClick={() => setSendDialogOpen(true)}
              disabled={sendingAgreement}
              sx={{
                bgcolor: '#f06292',
                color: '#fff',
                '&:hover': { bgcolor: '#ec407a' }
              }}
            >
              Send
            </Button>
          )}
        </Box>
      </Box>
      <PortalSnackbar
        open={Boolean(success)}
        autoHideDuration={3200}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </PortalSnackbar>

      <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            pt: 0,
            px: 0,
            pb: { xs: 2, md: 3 }
          }}
        >
          <ProposalCraftEditor
            key={`${selectedTemplateId || (isEdit ? id : 'new')}:${loadingTemplate ? 'loading' : 'ready'}`}
            blocks={formData.content?.blocks || []}
            onBlocksChange={(blocks) => setFormData((p) => ({ ...p, content: { blocks } }))}
            editorRef={craftEditorRef}
            showInvoicingTab={false}
            recipientSlot={(
              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Recipient
                </Typography>
                <LeadCoupleAutocomplete
                  businessId={businessId}
                  value={formData.leadId}
                  onChange={(leadId) => setFormData((p) => ({ ...p, leadId: leadId || '' }))}
                  label="Select couple"
                  required
                  disabled={isEdit}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Message to recipient"
                  margin="normal"
                  multiline
                  minRows={3}
                  value={formData.message}
                  onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
                />
              </Paper>
            )}
            termsText={formData.termsText}
            lineItems={formData.lineItems}
            billingSchedule={formData.billingSchedule}
            currency={formData.currency}
            onAddLineItem={() => setLineItemModal({ open: true, index: null })}
            onEditLineItem={(idx) => setLineItemModal({ open: true, index: idx })}
            onRemoveLineItem={(idx) =>
              setFormData((prev) => ({
                ...prev,
                lineItems: prev.lineItems.filter((_, i) => i !== idx)
              }))
            }
            onAddSchedule={() => setScheduleModal({ open: true, index: null })}
            onEditSchedule={(idx) => setScheduleModal({ open: true, index: idx })}
            onRemoveSchedule={(idx) =>
              setFormData((prev) => ({
                ...prev,
                billingSchedule: prev.billingSchedule.filter((_, i) => i !== idx)
              }))
            }
            onEditTerms={() => setTermsModalOpen(true)}
            additionalInformationFields={formData.additionalInformationFields}
            onAdditionalInformationFieldsChange={(fields) =>
              setFormData((p) => ({ ...p, additionalInformationFields: fields }))
            }
          />
        </Box>

      </Box>

      <ProposalLineItemModal
        open={lineItemModal.open}
        onClose={() => setLineItemModal({ open: false, index: null })}
        initial={lineItemModal.index !== null ? formData.lineItems[lineItemModal.index] : null}
        onSave={handleSaveLineItem}
        products={products}
        title={lineItemModal.index === null ? 'Add line item' : 'Edit line item'}
      />
      <ProposalBillingScheduleModal
        open={scheduleModal.open}
        onClose={() => setScheduleModal({ open: false, index: null })}
        initial={scheduleModal.index !== null ? formData.billingSchedule[scheduleModal.index] : null}
        onSave={handleSaveSchedule}
        title={scheduleModal.index === null ? 'Add schedule item' : 'Edit schedule item'}
      />
      <ProposalTermsModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        value={formData.termsText}
        onSave={(text) => setFormData((p) => ({ ...p, termsText: text }))}
      />
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send agreement</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This will send the agreement immediately.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Recipient
              </Typography>
              <Typography variant="body2">
                {sendRecipient}
              </Typography>
              {sendRecipientEmail && sendRecipientEmail !== sendRecipient && (
                <Typography variant="caption" color="text.secondary">
                  {sendRecipientEmail}
                </Typography>
              )}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Pricing breakdown
              </Typography>
              {sendLineItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No line items added.
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {sendLineItems.map((item, index) => {
                    const quantity = Number(item.quantity) || 1;
                    const unitWithTax = getUnitPriceWithTax(item);
                    const tax = parseFloat(item.taxPercentage) || 0;
                    return (
                      <Box key={`${item.description}-${index}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2">
                            {(item.description || '').trim()}
                            {item.isOptional ? ' (optional)' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {quantity} x {formatMoney(unitWithTax, formData.currency)}
                            {tax > 0 ? (item.priceIncludesTax ? ' incl. VAT' : ` + ${tax}% VAT`) : ''}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                          {formatMoney(getLineItemTotal(item), formData.currency)}
                        </Typography>
                      </Box>
                    );
                  })}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight={600}>
                      Total
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {formatMoney(Math.round(sendTotal * 100) / 100, formData.currency)}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Paper>
            <Button
              variant="contained"
              onClick={handleSendNow}
              disabled={sendingAgreement}
            >
              {sendingAgreement ? <CircularProgress size={20} /> : sendNowLabel}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)} disabled={sendingAgreement}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
}

export default ProposalForm;
